import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Channel, Me, Message, ReactionName, UnreadPlace } from '../api/types'
import { api } from '../api/client'
import { useAppHeight } from '../app/useAppHeight'
import { useViewport } from '../app/useViewport'
import { Badge, FALLBACK_FACE_STYLE, Face, FacePile, FaceProvider, isFaceStyle } from '../components/primitives'
import type { FaceSetView } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { Composer } from '../composer/Composer'
import { useColumnWidth } from './ColumnResizer'
import { BottomNav, type Tab } from '../navigation/BottomNav'
import { Sidebar } from '../navigation/Sidebar'
import { agentsIn, shortName, spokenName } from '../navigation/agents'
import { Help, Palette } from '../navigation/Palette'
import { useKeyboard } from '../navigation/useKeyboard'
import { ClaimsModal } from '../claims/ClaimsModal'
import { claimsButtonState } from '../claims/claims'
import { useClaims } from '../claims/useClaims'
import { StatusModal } from '../status/StatusModal'
import { buttonState } from '../status/status'
import { useRoomStatus } from '../status/useRoomStatus'
import { Settings } from '../settings/Settings'
import { SetupPane } from '../settings/Setup'
import { Lessons } from '../lessons/Lessons'
import { Overlay } from '../components/Overlay'
import { consumeGithubLink, type GithubLinkOutcome } from '../settings/githubLink'
import { consumeGoogleLink, type GoogleLinkOutcome } from '../settings/googleLink'
import type { Pane as SettingsPane } from '../settings/Settings'
import { InstallCommand } from '../install/InstallCommand'
import { NoMachineNotice } from '../install/NoMachineNotice'
import { DirectView, asRosterAgent } from '../direct/DirectView'
import { openAt } from '../read/openAt'
import { useSeen } from '../read/useSeen'
import { unreadPlaces, unreadTotal } from '../unread/counts'
import { UnreadsView } from '../unread/UnreadsView'
import { ActivityView } from './ActivityView'
import {
  hasHistory, horizonLine, oldestHeldOf, roomMessagesOf, roomName, sinceLabel, threadRootOf,
} from './format'
import { Lane, type LaneContent } from './Lane'
import { Profile, ProfileSheet } from './Profile'
import { RoomMembers } from './RoomMembers'
import { SearchBar } from './SearchBar'
import { NEW_LINE_ATTRIBUTE, PINNED_WITHIN, READING_WIDTH, SELECTED_ATTRIBUTE, Stream, streamPadding } from './Stream'
import { Thread } from './Thread'
import { useRoom } from './useRoom'
import { useRooms } from './useRooms'


/**
 * The sidebar, and how far it may be dragged. It keeps the repository list's
 * key, so a width somebody chose survives the change. The bounds are where it
 * stops doing its job: narrower cannot show a room name beside its counts, and
 * wider is a list of short names in a wide empty column.
 */
const SIDEBAR = { key: 'stagepost.width.repositories', width: 252, min: 200, max: 380 }

/**
 * One room per repo. No sub-channels: replies carry all sub-structure.
 *
 * What rooms exist and what is in one are `useRooms` and `useRoom`. What is
 * left here is the interface itself: where things sit at each width, what is
 * open, what is selected, and which keys do what.
 */
/** What a phone's back control returns to, by the name its tab carries. */
const TAB_NAMES = { home: 'Home', direct: 'DMs', activity: 'Activity', unreads: 'Unreads' } as const

type PhoneTab = keyof typeof TAB_NAMES

/**
 * What the main column shows. `people` is the roster: a popover over the room
 * on a wide screen and the whole screen on a phone. `activity` and `unreads`
 * take the room's place at every width. `home` and `direct` are a phone's sidebar
 * screens; on a wide screen the sidebar is always there and they show the room.
 * `{kind: 'direct'}` is one agent's direct message, in the room's place;
 * `fromRoom` when a phone opened it from the room, which its back control
 * then returns to.
 */
type View = 'room' | 'people' | PhoneTab | { kind: 'direct'; agent: string; fromRoom: boolean }

/** A phone's way back to the room, or to the tab a room or a direct message was opened from. */
function BackButton({ to, onClick }: { to: PhoneTab | 'room'; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Back to ${to === 'room' ? 'the room' : TAB_NAMES[to]}`}
      className="sp-tap"
      style={{ display: 'flex', flex: 'none', padding: 4, cursor: 'pointer', color: 'var(--text-soft)' }}
    >
      <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M7.5 2.5 4 6l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  )
}

export function Workspace({ me, onMeChanged }: { me: Me; onMeChanged?: (me: Me) => void }) {
  const rooms = useRooms()
  const { compact, narrow } = useViewport()
  const {
    messages, roster, plans, unread, newLineBefore, markSeen, markRead, loadFailed, post, replaceMessage, onRoomFrame,
  } = useRoom(rooms.channel, me.display_name)

  /** What the right column shows on a desktop, and the sheet on a phone. */
  const [lane, setLane] = useState<LaneContent | null>(null)
  /**
   * Coming back from a GitHub link is a full-page redirect, so the pane the
   * button was pressed in is gone. Reopening it is the difference between an
   * answer and a page that looks like nothing happened.
   */
  const [githubLink, setGithubLink] = useState<GithubLinkOutcome | null>(null)
  const [googleLink, setGoogleLink] = useState<GoogleLinkOutcome | null>(null)
  const [overlay, setOverlay] = useState<'palette' | 'help' | 'settings' | 'lessons' | 'setup' | null>(null)
  /** Which pane settings opens on, when something outside it decided. */
  const [settingsPane, setSettingsPane] = useState<SettingsPane | undefined>(undefined)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [view, setView] = useState<View>(compact ? 'home' : 'room')
  /** The phone tab a room was opened from, which its back control returns to. */
  const [cameFrom, setCameFrom] = useState<PhoneTab>('home')
  const [mentionRequest, setMentionRequest] =
    useState<{ name: string; nonce: number } | null>(null)
  const [statusOpen, setStatusOpen] = useState(false)
  const [claimsOpen, setClaimsOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  /** The last search hit opened is further back than the loaded messages. */
  const [searchMiss, setSearchMiss] = useState(false)
  /** The header's Start agent, until the composer has put its `/agent` chip in. */
  const [agentRequested, setAgentRequested] = useState(false)
  /**
   * A message whose thread opens once the room it was said in has loaded, from
   * Activity or a direct message. Activity opens it for a reply whose thread
   * the page holds from the start; `always`, from a direct message, opens it
   * for any message, since that is where a reply to it is written. `select`,
   * from the Unreads list, selects any message it does not open a thread for.
   */
  const [threadOf, setThreadOf] = useState<{ messageId: string; always: boolean; select?: boolean } | null>(null)
  const { status, saveStatus } = useRoomStatus(rooms.channel, onRoomFrame)
  const { claims } = useClaims(rooms.channel, onRoomFrame)

  useAppHeight()
  const sidebarWidth = useColumnWidth(SIDEBAR.key, SIDEBAR.width, SIDEBAR.min, SIDEBAR.max)
  const streamRef = useRef<HTMLDivElement | null>(null)
  const streamSizeObserver = useRef<ResizeObserver | null>(null)
  // Whether the reader is at the live edge. A room that yanks you to the bottom
  // while you are reading three days back is worse than one that never scrolls.
  const pinned = useRef(true)
  // Where a reader away from the live edge was, for the stream to come back
  // to when it mounts again: a phone's room behind a thread, or a desktop's
  // behind Activity.
  const scrolledTo = useRef(0)
  // The New line the room last opened at. A room opens there once per load,
  // so a stream that mounts again, as on a phone back from a thread, does not
  // jump back to it.
  const openedAt = useRef<string | null>(null)
  useSeen(streamRef, unread, markSeen)

  const { organisations, organisation, channels, activity } = rooms
  const agents = rooms.agents ?? []
  const currentChannel = rooms.channel

  // Every face in the tree resolves through this rather than through a prop
  // threaded past seventeen call sites. An unknown style falls back rather
  // than rendering a broken image everywhere at once.
  const faces = useMemo(
    () => ({
      ...Object.fromEntries(roster.map((agent) => [agent.name, agent.avatar_key])),
      // Yourself too: your messages are in the stream under your display name,
      // and a chosen face that shows everywhere except on your own messages
      // reads as not having saved.
      [me.display_name]: me.avatar_key,
    }),
    [roster, me.display_name, me.avatar_key],
  )
  // `face_style` is either one of the five built-ins or `set:<slug>`, and the
  // server enforces that shape, so this carries the raw string through rather
  // than narrowing it: `isFaceStyle` would discard `set:<slug>` and put every
  // organisation on its own pool back on the built-in floor.
  const faceStyle = organisation?.face_style ?? FALLBACK_FACE_STYLE
  const faceSet: FaceSetView | null = useMemo(() => {
    const set = organisation?.face_set
    if (!set) return null
    return {
      slug: set.slug,
      // The server enforces that a fallback names a built-in
      // (`_check_fallback_style`), but the wire type is a plain string, so this
      // is the one place that fact is worth checking rather than trusted.
      fallbackStyle: isFaceStyle(set.fallback_style) ? set.fallback_style : FALLBACK_FACE_STYLE,
      slots: Object.fromEntries(set.slots.map((slot) => [slot.slot, { url: slot.url, name: slot.name }])),
    }
  }, [organisation?.face_set])

  const scrollToNewest = useCallback(() => {
    pinned.current = true
    const node = streamRef.current
    if (node) node.scrollTop = node.scrollHeight
  }, [])

  /**
   * The stream gives up height to whatever grows beneath it, and the composer
   * grows with every line of a draft. A reader at the live edge stays there:
   * without this the newest message, usually the one being answered, slid out
   * of view one line at a time as the answer got longer. The same holds for a
   * keyboard opening or the window shrinking.
   *
   * A callback ref rather than an effect, because the stream mounts and
   * unmounts with the room and with the compact view, and this is the one
   * place that hears about every mount.
   */
  const attachStream = useCallback((node: HTMLDivElement | null) => {
    streamRef.current = node
    streamSizeObserver.current?.disconnect()
    streamSizeObserver.current = null
    if (!node) return
    if (!pinned.current) node.scrollTop = scrolledTo.current
    const observer = new ResizeObserver(() => {
      if (pinned.current) node.scrollTop = node.scrollHeight
    })
    observer.observe(node)
    streamSizeObserver.current = observer
  }, [])

  /**
   * In an effect, not in a `useState` initializer.
   *
   * Reading the address bar *and taking the parameter out of it* is a side
   * effect, and React invokes an initializer twice in development: the first
   * call consumed the parameter and the second, running against the URL the
   * first had already cleaned, decided nothing had happened. Nothing opened,
   * in dev only, which is the worst place for a bug to hide. Here the second
   * run finds nothing and is ignored, because only a real outcome writes.
   */
  useEffect(() => {
    const outcome = consumeGithubLink()
    if (!outcome) return
    setGithubLink(outcome)
    setOverlay('settings')
  }, [])

  // Same reasoning, Google's outcome in place of GitHub's.
  useEffect(() => {
    const outcome = consumeGoogleLink()
    if (!outcome) return
    setGoogleLink(outcome)
    setOverlay('settings')
  }, [])

  useEffect(() => {
    // Only follow the live edge for someone already at it.
    if (pinned.current) scrollToNewest()
  }, [messages.length, scrollToNewest])

  // A room switch lands at the bottom until its page says where the New line is.
  useEffect(() => {
    openedAt.current = null
    scrollToNewest()
  }, [currentChannel?.id, scrollToNewest])

  const scrollToNewLine = useCallback(() => {
    const node = streamRef.current
    if (node) pinned.current = openAt(node, node.querySelector(`[${NEW_LINE_ATTRIBUTE}]`))
  }, [])

  /** Only real messages the stream shows are selectable: a merge event has nothing to open, and a reply is in its thread. */
  const selectable = useMemo(
    () => roomMessagesOf(messages).filter((message) => message.sender_type !== 'event'),
    [messages],
  )

  const moveSelection = useCallback((direction: 1 | -1) => {
    if (!selectable.length) return
    pinned.current = false
    setSelectedId((previous) => {
      const at = selectable.findIndex((message) => message.id === previous)
      if (at < 0) return direction > 0 ? selectable[0].id : selectable[selectable.length - 1].id
      const next = Math.min(selectable.length - 1, Math.max(0, at + direction))
      return selectable[next].id
    })
  }, [selectable])

  // Centre whatever j/k landed on. Done after paint so the row it is looking
  // for has been marked.
  useEffect(() => {
    if (!selectedId) return
    const container = streamRef.current
    const row = container?.querySelector<HTMLElement>(`[${SELECTED_ATTRIBUTE}="yes"]`)
    if (container && row) {
      container.scrollTop = row.offsetTop - container.clientHeight / 2 + row.offsetHeight / 2
    }
  }, [selectedId])

  /**
   * The thread `message` is in, where a reply to it is written: in the right
   * column, or on its own screen on a phone. It opens on the oldest message of
   * it the page holds, which is the first unless the page starts after it.
   */
  function openThread(message: Message) {
    setLane({ kind: 'thread', rootId: oldestHeldOf(messages, message).id })
  }

  /** On a phone a profile is a sheet over the thread it was opened from, which closing it returns to. */
  function closeLane() {
    setLane(compact && lane?.kind === 'profile' && lane.over ? { kind: 'thread', rootId: lane.over } : null)
  }

  // A thread asked for from another view opens once its room has loaded what it holds.
  useEffect(() => {
    if (!threadOf) return
    const message = messages.find((candidate) => candidate.id === threadOf.messageId)
    if (!message) return
    setThreadOf(null)
    if (threadOf.always || (message.reply_to && threadRootOf(messages, message))) openThread(message)
    else if (message.reply_to || threadOf.select) {
      // Its thread starts before the page, or it is a first message, so the room opens at it instead.
      pinned.current = false
      setSelectedId(message.id)
    }
  }, [messages, threadOf])

  function replyToSelected() {
    const message = selectable.find((candidate) => candidate.id === selectedId)
    if (message) openThread(message)
  }

  function focusComposer() {
    const box = document.querySelector<HTMLTextAreaElement>(
      'textarea[aria-label="Say something to the room"]')
    box?.focus()
  }

  useKeyboard({
    openPalette: () => setOverlay(overlay === 'palette' ? null : 'palette'),
    openHelp: () => setOverlay('help'),
    focusComposer,
    moveSelection,
    replyToSelected,
    jumpToNewest: scrollToNewest,
    // Escape unwinds one layer at a time, outermost first: an overlay, then
    // search, then the roster, then the right column, then activity or the Unreads list, then the selection.
    // A phone's tabs are places, not layers, so Escape leaves them alone.
    escape: () => {
      if (overlay) setOverlay(null)
      else if (searchOpen) setSearchOpen(false)
      else if (view === 'people') setView('room')
      else if (lane) closeLane()
      else if ((view === 'activity' || view === 'unreads') && !compact) setView('room')
      else if (selectedId) setSelectedId(null)
    },
    // Same landing as clicking the room in the sidebar. Switching by keyboard and
    // switching by mouse must not leave the app in two different states.
    selectRoom: (index) => { if (channels[index]) goToRoom(channels[index]) },
  })

  async function say(body: string) {
    await post(body)
    scrollToNewest()
  }

  /** `held` comes from the chip, so the verb is decided without asking again. */
  async function toggleReaction(message: Message, reaction: ReactionName,
                                held: boolean) {
    const open = currentChannel
    if (!open) return
    const updated = held
      ? await api.unreact(open.id, message.id, reaction)
      : await api.react(open.id, message.id, reaction)
    replaceMessage(updated)
  }

  /**
   * A search hit selects its message in the stream, which scrolls it into
   * view, when it is loaded. A reply the stream does not show opens its thread.
   */
  function openSearchResult(messageId: string) {
    const message = messages.find((candidate) => candidate.id === messageId)
    setSearchMiss(!message)
    if (!message) return
    pinned.current = false
    setSelectedId(messageId)
    setSearchOpen(false)
    if (!roomMessagesOf(messages).includes(message)) openThread(message)
  }

  /** Arriving anywhere puts the interface back to a resting state. */
  function rest() {
    setSelectedId(null)
    setThreadOf(null)
    setLane(null)
    setSearchOpen(false)
  }

  /** Into the room or a direct message, remembering which phone tab to go back to. */
  function enter(next: View = 'room') {
    if (view === 'home' || view === 'direct' || view === 'activity' || view === 'unreads') setCameFrom(view)
    setView(next)
  }

  function goToRoom(channel: Channel) {
    rooms.open(channel)
    rest()
    enter()
  }

  function openDirect(agent: string) {
    rest()
    enter({ kind: 'direct', agent, fromRoom: view === 'room' })
  }

  function ownsAgent(name: string) {
    return agents.some((agent) => agent.name === name)
  }

  /** Over the room, from a face, a name or the roster. */
  function openProfile(name: string) {
    setView('room')
    setLane({ kind: 'profile', name, over: compact && lane?.kind === 'thread' ? lane.rootId : undefined })
  }

  /** Activity takes the room's place, and what the right column held was about the room. */
  function openActivity() {
    setLane(null)
    setView('activity')
  }

  /** In the room's place on a desktop, as Activity is; on a phone a screen whose back control goes Home. */
  function openUnreads() {
    setLane(null)
    setView('unreads')
  }

  /** From the Unreads list: the room at its New line, even when it is the room already open, or the direct message. */
  function openPlace(place: UnreadPlace) {
    if (place.kind === 'direct') {
      openDirect(place.agent)
      return
    }
    const channel = channels.find((candidate) => candidate.id === place.channel_id)
    if (!channel) return
    openedAt.current = null
    goToRoom(channel)
  }

  /** A room message at itself, a reply in its thread once the room holds it, a private message in the direct message. */
  function openUnread(place: UnreadPlace, message: Message) {
    openPlace(place)
    if (place.kind === 'room') setThreadOf({ messageId: message.id, always: false, select: true })
  }

  /** From a direct message, into the room on the thread `message` is in, its first message's own included. */
  function openThreadIn(channelId: string, message: Message) {
    const channel = channels.find((candidate) => candidate.id === channelId)
    if (!channel) return
    goToRoom(channel)
    setThreadOf({ messageId: message.id, always: true })
  }

  /** Another organisation: a wide screen shows its room, a phone stays on the tab it was on. */
  function leaveOrganisation() {
    rest()
    if (!compact) setView('room')
  }

  function openSearch() {
    enter()
    setSearchMiss(false)
    setSearchOpen(true)
    document.querySelector<HTMLInputElement>('input[aria-label="Search this room"]')?.focus()
  }

  // The room's own running agents: a visitor from another room is not one.
  const online = roster.filter((agent) => agent.online && !agent.visiting_from)
  // Yours that wait on you here, and everyone else's live agents as working.
  const waitingHere = agents.filter((agent) =>
    agent.channel_id === currentChannel?.id && agent.waiting > 0)
  const working = online.filter((agent) =>
    !waitingHere.some((waiting) => waiting.name === agent.name)).length
  const statusSignal = buttonState(status)
  // How old the status is. "never set" says nothing worth a glance beside the
  // agents working, so only an age rides on the topic line.
  const statusAge = !statusSignal.urgent && status?.sections.length ? statusSignal.note : null

  const direct = typeof view === 'object' ? view.agent : null
  const directBack = typeof view === 'object' && view.fromRoom ? 'room' : cameFrom
  const yoursHere = compact ? agentsIn(agents, currentChannel?.id) : []
  const threadRootId = lane?.kind === 'thread' ? lane.rootId : compact ? lane?.over : undefined
  const threadRoot = messages.find((message) => message.id === threadRootId)
  // On a phone an open thread is a screen in the room's place.
  const roomVisible = !direct && (compact ? view === 'room' && !threadRoot : view !== 'activity' && view !== 'unreads')

  // Once its own page has landed and the stream is on screen, a room with
  // something unread in it opens at its New line, and is kept at the bottom
  // only when it opened there.
  useEffect(() => {
    if (!streamRef.current || !newLineBefore || openedAt.current === newLineBefore) return
    openedAt.current = newLineBefore
    scrollToNewLine()
  }, [newLineBefore, roomVisible, scrollToNewLine])

  // The phone's bar: the stream's count for the room, which the page may not
  // hold all of, and how much of what the page holds is in threads.
  const firstUnread = compact ? messages.find((message) => unread.has(message.id)) : undefined
  const unreadInThreads = firstUnread
    ? unread.size - roomMessagesOf(messages).filter((message) => unread.has(message.id)).length
    : 0
  // "Since" stays where the New line is, as the line does, while the count drops with reading.
  const missedSince = firstUnread && (messages.find((message) => message.id === newLineBefore) ?? firstUnread)

  const settingsOpen = overlay === 'settings'

  const panel: CSSProperties = {
    flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    // The status modal is absolute inside the room, not over the whole
    // window: the sidebar stays usable behind it.
    position: 'relative',
    background: compact ? 'transparent' : 'var(--panel)',
    border: compact ? 'none' : '1px solid var(--border)',
    borderRadius: compact ? 0 : radius.lg,
    paddingBottom: compact ? 'var(--safe-bottom)' : 0,
  }

  /** Back in the room with `@<name>` in the composer. */
  function mention(name: string) {
    setLane(null)
    setView('room')
    setMentionRequest({ name, nonce: Date.now() })
  }

  // Held until the organisation stream says which agents are the person's own:
  // before that, one's own agent would show as a stranger's.
  const profile = lane?.kind === 'profile' && rooms.agents !== null && (
    <Profile
      name={lane.name}
      agent={roster.find((agent) => agent.name === lane.name)}
      yours={rooms.agents.find((agent) => agent.name === lane.name)}
      plan={plans[lane.name]}
      channels={channels}
      compact={compact}
      onDirectMessage={openDirect}
      onMention={mention}
    />
  )

  // What a message row does, the same in the room and in a thread.
  const messageRows = {
    messages, unread, roster, myName: me.display_name, onOpenProfile: openProfile, onReact: toggleReaction,
    onOpenThread: openThread, onMention: mention, onOpenDirect: openDirect, ownsAgent,
    onAnswer: (message: Message, answer: string) => post(`@${message.sender} ${answer}`, message.id),
  }

  const thread = threadRoot && currentChannel && (
    <Thread
      {...messageRows}
      key={threadRoot.id}
      root={threadRoot}
      room={roomName(currentChannel)}
      channelId={currentChannel.id}
      back={compact ? <BackButton to="room" onClick={() => setLane(null)} /> : undefined}
      narrow={narrow}
      onSeen={markSeen}
      onSend={async (body, alsoInRoom) => { await post(body, threadRoot.id, alsoInRoom) }}
    />
  )

  // A popover over the room on a wide screen, the screen itself on a phone.
  const members = view === 'people' ? (
    <RoomMembers
      placement={compact ? 'screen' : 'popover'}
      roster={roster}
      plans={plans}
      messages={messages}
      myName={me.display_name}
      linkedRepository={currentChannel?.github_full_name ?? null}
      onChoose={openProfile}
      onClose={() => setView('room')}
    />
  ) : null

  // Carries its own signal: a modal hides staleness, and a dot on a button
  // does not.
  const filesAction = (
    <button
      onClick={() => setClaimsOpen(true)}
      aria-label="Who is changing what"
      className="sp-tap"
      style={{
        display: 'flex', alignItems: 'center', gap: 7, flex: 'none', cursor: 'pointer',
        border: '1px solid var(--border-strong)', background: 'var(--panel)',
        borderRadius: radius.action, padding: '5px 12px',
        color: 'var(--text-soft)', font: '600 12px var(--font-interface)',
      }}
    >
      {claimsButtonState(claims).urgent && (
        <span style={{
          width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)',
          boxShadow: '0 0 0 3px var(--accent-wash)', flex: 'none',
        }} />
      )}
      Files
      {claimsButtonState(claims).note && (
        <span className="sp-header-note" style={{ font: '400 var(--size-meta)/1 var(--font-machine)', color: 'var(--text-meta)' }}>
          {claimsButtonState(claims).note}
        </span>
      )}
    </button>
  )

  const startAgentAction = (
    <button
      type="button"
      onClick={() => setAgentRequested(true)}
      className="sp-tap"
      style={{
        display: 'flex', alignItems: 'center', flex: 'none', cursor: 'pointer',
        background: 'var(--text)', borderRadius: radius.sm, padding: '6px 12px',
        color: 'var(--ground)', font: '600 12px var(--font-interface)',
      }}
    >
      Start agent
    </button>
  )

  return (
    <FaceProvider style={faceStyle} faces={faces} set={faceSet}>
    <div
      style={{
        position: 'relative', zIndex: 1, display: 'flex', width: '100%',
        // Never `100vh` — see `useAppHeight`. On a phone that measures a
        // viewport with the browser's own toolbars hidden, so the composer at
        // the foot of this shell sits behind the address bar with nothing to
        // scroll.
        height: 'var(--app-height)',
        gap: compact ? 0 : 12, padding: compact ? 0 : 14,
        // Compact paints to the edges, so the notch and the home indicator are
        // the app's to keep clear rather than the browser's.
        paddingLeft: compact ? 'var(--safe-left)' : 14,
        paddingRight: compact ? 'var(--safe-right)' : 14,
      }}
    >
      {!compact && (
        <Sidebar
          layout="column" column={sidebarWidth} me={me} rooms={rooms} directOpen={direct} unreadsOpen={view === 'unreads'}
          onOpenRoom={goToRoom} onOpenDirect={openDirect} onUnreads={openUnreads} onActivity={openActivity}
          onSearch={openSearch} onOpen={setOverlay} onLeave={leaveOrganisation}
        />
      )}
      {compact && (view === 'home' || view === 'direct') && (
        <Sidebar
          layout={view} me={me} rooms={rooms}
          onOpenRoom={goToRoom} onOpenDirect={openDirect} onUnreads={openUnreads} onActivity={openActivity}
          onSearch={openSearch} onOpen={setOverlay} onLeave={leaveOrganisation}
        />
      )}

      {direct && organisation && (
        <main style={panel}>
          <DirectView
            key={direct}
            organisation={organisation.slug}
            name={direct}
            agent={agents.find((agent) => agent.name === direct)}
            channels={channels}
            myName={me.display_name}
            back={compact && <BackButton to={directBack} onClick={() => setView(directBack)} />}
            onOrganisationFrame={rooms.onOrganisationFrame}
            openChannelId={currentChannel?.id ?? null}
            onOpenRoomFrame={onRoomFrame}
            onOpenThread={openThreadIn}
          />
        </main>
      )}

      {/* The room */}
      {roomVisible && (
        <main style={panel}>
          <header
            className="sp-room-header"
            style={{
              padding: compact
                ? 'calc(10px + var(--safe-top)) 16px 11px'
                : '14px 20px',
              borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', gap: compact ? 10 : 12,
              flex: 'none', minWidth: 0,
            }}
          >
            {compact && <BackButton to={cameFrom} onClick={() => setView(cameFrom)} />}
            {/* Stacked on a phone, where the title row has no width for both. */}
            <div style={{
              display: 'flex', flexDirection: compact ? 'column' : 'row', alignItems: compact ? 'stretch' : 'center',
              gap: compact ? 1 : 12, flex: '0 1 auto', minWidth: 0,
            }}>
            <h1
              title={currentChannel?.repository_key}
              style={{
                margin: 0, font: '600 16px var(--font-machine)', letterSpacing: '-.03em',
                flex: '0 1 auto', minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              <span aria-hidden="true"># </span>{currentChannel ? roomName(currentChannel) : 'no room'}
            </h1>
            {/* The topic line, and the way to the room's status. The dot and
                the count are the status's own signal (see `buttonState`):
                without them the blocker that has been waiting three weeks is
                buried in a dialog nobody opens. */}
            <button
              type="button"
              onClick={() => setStatusOpen(true)}
              title="Room status"
              className="sp-tap"
              style={{
                display: 'flex', alignItems: 'center', gap: 7, flex: '0 1 auto', minWidth: 0,
                cursor: 'pointer', padding: 0, font: '400 12px var(--font-interface)', color: 'var(--text-meta)',
              }}
            >
              {statusSignal.urgent && (
                <span style={{
                  width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)',
                  boxShadow: '0 0 0 3px var(--accent-wash)', flex: 'none',
                }} />
              )}
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {working} {working === 1 ? 'agent' : 'agents'} working
                {waitingHere.length > 0 && (
                  <> · <span style={{ color: 'var(--accent)' }}>{waitingHere.length} waiting on you</span></>
                )}
                {statusSignal.urgent && ` · ${statusSignal.note}`}
                {statusAge && <span className="sp-header-note"> · {statusAge}</span>}
              </span>
            </button>
            </div>
            <span style={{ flex: 1, minWidth: 8 }} />
            <button
              type="button"
              onClick={() => setView(view === 'people' ? 'room' : 'people')}
              aria-label={`${online.length} in the room`}
              aria-expanded={view === 'people'}
              className="sp-tap"
              style={{
                display: 'flex', alignItems: 'center', gap: 7, flex: 'none', cursor: 'pointer',
                border: '1px solid var(--border-strong)', borderRadius: radius.action,
                padding: online.length ? '3px 10px 3px 4px' : '5px 10px',
              }}
            >
              <FacePile names={online.map((agent) => agent.name)} limit={4} size={20} />
              <span style={{ font: '600 12px var(--font-machine)', color: 'var(--text-soft)' }}>
                {online.length}
              </span>
            </button>
            {/* A phone has no width for them beside the name; the composer's /agent and /files reach both. */}
            {!compact && startAgentAction}
            {!compact && filesAction}
          </header>

          {missedSince && (
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 12, flex: 'none', padding: '9px 16px',
                background: 'var(--accent-wash)', borderBottom: '1px solid var(--accent-edge)',
                font: '600 12.5px var(--font-interface)', color: 'var(--accent)',
              }}
            >
              <button
                type="button"
                onClick={scrollToNewLine}
                style={{
                  flex: 1, minWidth: 0, padding: 0, textAlign: 'left', cursor: 'pointer',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {Math.max(currentChannel?.unread ?? 0, unread.size)} new since {sinceLabel(missedSince.posted_at)}
                {unreadInThreads > 0 && ` · ${unreadInThreads} in threads`}
              </button>
              <button type="button" onClick={() => markRead()} className="sp-tap" style={{ flex: 'none', padding: 0, cursor: 'pointer', fontWeight: 700 }}>
                Mark read
              </button>
            </div>
          )}

          {yoursHere.length > 0 && (
            <ul
              aria-label="Your agents here"
              className="sp-scroller"
              style={{
                display: 'flex', gap: 12, flex: 'none', margin: 0, padding: '12px 16px', listStyle: 'none',
                borderBottom: '1px solid var(--border)',
              }}
            >
              {yoursHere.map((agent) => (
                <li key={agent.name} style={{ flex: 'none' }}>
                  <button
                    type="button"
                    onClick={() => openDirect(agent.name)}
                    className="sp-tap"
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, width: 64,
                      padding: 0, cursor: 'pointer', font: '500 11px var(--font-machine)',
                    }}
                  >
                    {/* The name comes first in the markup, so the button is called by it before the
                        waiting count, and the face, drawn above it, says the handle no second time. */}
                    <span
                      title={agent.name}
                      aria-label={spokenName(agent)}
                      style={{
                        maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-soft)',
                        fontFamily: agent.nickname ? 'var(--font-interface)' : undefined,
                      }}
                    >
                      {shortName(agent)}
                    </span>
                    <span style={{ position: 'relative', order: -1 }}>
                      <span aria-hidden="true" style={{ display: 'flex' }}>
                        <Face name={agent.name} size={40} online={agent.online} avatarKey={agent.avatar_key} />
                      </span>
                      <span style={{ position: 'absolute', top: -3, right: -5 }}>
                        <Badge count={agent.waiting} label={`${agent.waiting} waiting on you`} />
                      </span>
                    </span>
                    {agent.plan && <span style={{ color: 'var(--online)' }}>{agent.plan.done}/{agent.plan.total}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {currentChannel && (
            <NoMachineNotice onShowSteps={() => { setSettingsPane('Install'); setOverlay('settings') }} />
          )}

          {/* An organisation with no rooms at all — which is what every new one
              is. Without this the header says "no room" over an empty stream,
              which is a dead end at exactly the moment somebody has just made
              the place and is looking for what to do next. */}
          {!currentChannel ? (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 30 }}>
              <div style={{ maxWidth: 470, minWidth: 0 }}>
                <p style={{ fontFamily: 'var(--font-human)', fontSize: 16, margin: '0 0 12px' }}>
                  No rooms in {organisation?.name ?? 'here'} yet.
                </p>
                <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-dim)', margin: '0 0 14px' }}>
                  A room appears when an agent first speaks in a repository.
                  Put the command on the machine it runs on:
                </p>
                <InstallCommand />
                <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-dim)', margin: '14px 0 0' }}>
                  Then run{' '}
                  <code style={{ font: '400 12.5px var(--font-machine)', color: 'var(--text)' }}>
                    stagepost login
                  </code>{' '}
                  and{' '}
                  <code style={{ font: '400 12.5px var(--font-machine)', color: 'var(--text)' }}>
                    stagepost init
                  </code>{' '}
                  inside the repository, or connect GitHub from{' '}
                  <button
                    type="button"
                    onClick={() => { setSettingsPane('GitHub'); setOverlay('settings') }}
                    style={{ padding: 0, background: 'none', cursor: 'pointer', font: 'inherit', fontWeight: 600, color: 'var(--accent-bright)' }}
                  >
                    settings
                  </button>.
                </p>
                {organisations.length > 1 && (
                  <p style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-faint)', marginTop: 14 }}>
                    You are in more than one organisation, so name this one when
                    you do:{' '}
                    <code style={{ font: '400 12px var(--font-machine)' }}>
                      stagepost init --organisation {organisation?.slug}
                    </code>
                  </p>
                )}
              </div>
            </div>
          ) : (
          <>
          {searchOpen && (
            <SearchBar
              channelId={currentChannel.id}
              onOpenMessage={openSearchResult}
              onClose={() => setSearchOpen(false)}
            />
          )}
          {searchOpen && searchMiss && (
            <p
              role="status"
              style={{
                flex: 'none', margin: 0, padding: '8px 22px', background: 'var(--panel)',
                borderBottom: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text-dim)',
              }}
            >
              That message is not loaded. It is further back than this room shows.
            </p>
          )}
          <div
            ref={attachStream}
            onScroll={(event) => {
              const node = event.currentTarget
              pinned.current =
                node.scrollHeight - node.scrollTop - node.clientHeight < PINNED_WITHIN
              scrolledTo.current = node.scrollTop
            }}
            style={{
              flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column',
              // The padding rather than a wrapper, so the scrollbar stays at
              // the room's edge and a wheel anywhere in it scrolls.
              padding: streamPadding(compact),
              // A flick past the newest message must not drag the app behind
              // it — see the note in `base.css`.
              overscrollBehaviorY: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div style={{ flex: 1, minHeight: 14 }} />
            <Stream
              {...messageRows}
              newLineBefore={newLineBefore}
              channelId={currentChannel.id}
              selectedId={selectedId}
              openThreadId={threadRoot?.id}
              horizon={
                messages.length && organisation
                  ? horizonLine(organisation.retention_days)
                  : null
              }
              loadFailed={loadFailed}
            />
          </div>

          <div style={{ width: '100%', maxWidth: READING_WIDTH, margin: '0 auto' }}>
            <Composer
              roster={roster}
              nobodyRunning={online.length === 0 && hasHistory(roster, messages)}
              narrow={narrow}
              mentionRequest={mentionRequest}
              onMentionInserted={() => setMentionRequest(null)}
              onSend={say}
              channelId={currentChannel.id}
              roomName={roomName(currentChannel)}
              onOpenStatus={() => setStatusOpen(true)}
              onOpenClaims={() => setClaimsOpen(true)}
              onOpenLessons={() => setOverlay('lessons')}
              onOpenSetup={() => setOverlay('setup')}
              agentRequested={agentRequested}
              onAgentChipShown={() => setAgentRequested(false)}
            />
          </div>
          </>
          )}

          {statusOpen && currentChannel && (
            <StatusModal
              status={status}
              roomName={roomName(currentChannel)}
              onClose={() => setStatusOpen(false)}
              onSave={saveStatus}
            />
          )}

          {claimsOpen && currentChannel && (
            <ClaimsModal
              claims={claims}
              roomName={roomName(currentChannel)}
              onClose={() => setClaimsOpen(false)}
            />
          )}

          {!compact && members}

          {compact && profile && <ProfileSheet onClose={closeLane}>{profile}</ProfileSheet>}
        </main>
      )}

      {!compact && profile && <Lane title="Profile" onClose={() => setLane(null)}>{profile}</Lane>}
      {!compact && thread && currentChannel && (
        <Lane title="Thread" subtitle={`# ${roomName(currentChannel)}`} onClose={() => setLane(null)}>{thread}</Lane>
      )}
      {compact && thread && (
        <main style={panel}>
          {thread}
          {profile && <ProfileSheet onClose={closeLane}>{profile}</ProfileSheet>}
        </main>
      )}

      {compact && members}

      {view === 'activity' && (
        <ActivityView
          activity={activity}
          roster={[...roster, ...agents]}
          compact={compact}
          onOpenItem={(item) => {
            const channel = channels.find((candidate) => candidate.id === item.channel_id)
            if (!channel) return
            goToRoom(channel)
            // A reply opens its thread, once the room holds it.
            setThreadOf({ messageId: item.message_id, always: false })
          }}
          onBack={() => enter()}
        />
      )}

      {view === 'unreads' && organisation && (
        <UnreadsView
          key={organisation.slug}
          organisation={organisation}
          counts={unreadTotal(channels, agents)}
          channels={channels}
          roster={[...roster, ...agents.map(asRosterAgent)]}
          myName={me.display_name}
          compact={compact}
          openRoom={currentChannel ? { channelId: currentChannel.id, markSeen, markRead } : undefined}
          back={compact && <BackButton to="home" onClick={() => setView('home')} />}
          onOpenPlace={openPlace}
          onOpenMessage={openUnread}
        />
      )}

      {compact && typeof view === 'string' && view !== 'room' && view !== 'people' && (
        <BottomNav
          // The list is reached from Home and is part of it.
          tab={settingsOpen ? 'settings' : view === 'unreads' ? 'home' : view}
          unreadPlaces={unreadPlaces(channels, agents)}
          waitingAgents={agents.filter((agent) => agent.waiting > 0).length}
          onSelect={(next: Tab) => {
            if (next === 'settings') { setOverlay('settings'); return }
            setOverlay(null)
            setView(next)
          }}
        />
      )}

      {overlay === 'palette' && (
        <Palette
          rooms={rooms.everywhere}
          currentChannelId={currentChannel?.id ?? null}
          onPick={(room) => { goToRoom(room.channel); setOverlay(null) }}
          onClose={() => setOverlay(null)}
        />
      )}
      {overlay === 'help' && <Help onClose={() => setOverlay(null)} />}
      {overlay === 'lessons' && organisation && (
        <Lessons organisation={organisation} compact={compact} onClose={() => setOverlay(null)} />
      )}
      {overlay === 'setup' && organisation && (
        <Overlay
          title="Setup"
          subtitle="what your machines run, and who you share it with"
          compact={compact}
          onClose={() => setOverlay(null)}
        >
          <SetupPane me={me} organisation={organisation} machines={rooms.machines} />
        </Overlay>
      )}
      {settingsOpen && organisation && (
        <Settings
          me={me}
          organisation={organisation}
          machines={rooms.machines}
          compact={compact}
          githubLink={githubLink}
          googleLink={googleLink}
          initialPane={settingsPane}
          onMeChanged={onMeChanged}
          onOrganisationChanged={rooms.organisationChanged}
          onLinksChanged={() => void rooms.reload(organisation.slug)}
          onOrganisationDeleted={() => {
            rooms.organisationDeleted(organisation.slug)
            setOverlay(null)
            leaveOrganisation()
          }}
          // Nothing below this is ours any more, and the cookie is already
          // gone. A reload lands on the marketing site, which is the honest
          // place to be.
          onAccountDeleted={() => window.location.reload()}
          // Same reason, and it also drops every open stream and cached room
          // rather than leaving them on screen behind a dead cookie.
          onSignedOut={() => window.location.reload()}
          onClose={() => {
            setOverlay(null)
            setSettingsPane(undefined)
          }}
        />
      )}
    </div>
    </FaceProvider>
  )
}
